import { isNative, isWeb } from "@stepapp/core";
import {
  Block,
  HeaderLeft,
  palette,
  SvgIcon,
  spacing,
  Touchable,
  useBreakpoints,
} from "@stepapp/ui";
import { usePathname, useRouter } from "expo-router";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { AppStorage } from "../../services/AppStorage";
import HeaderButton from "./HeaderButton";
import HeaderDropdownButton from "./HeaderDropdownButton";
import PickCityButton from "./PickCityButton";
import { NavigationItem } from "./useNavigationItems";

interface HeaderContentProps {
  navigationItems: NavigationItem[];
  handleBackPress: () => void;
  setModalVisible: (visible: boolean) => void;
}

const HeaderContent: React.FC<HeaderContentProps> = ({
  navigationItems,
  handleBackPress,
  setModalVisible,
}) => {
  const { t } = useTranslation();
  const { md } = useBreakpoints();
  const pathname = usePathname();
  const { replace, navigate } = useRouter();
  const isAuthenticated = !!AppStorage().get("token");

  const showMobileNavigation = (!md && isWeb) || isNative;
  const showMenu = !pathname.includes("/assigned-worker");
  const backButtonVisible = useMemo(() => {
    if (isWeb) return pathname !== "/";
    return !["/", "/calendar", "/my-home"].includes(pathname);
  }, [pathname]);

  const navigateToLanding = useCallback(() => {
    if (isNative || pathname === "/") return;
    replace("/");
  }, [replace, pathname]);

  const navigateToCalendar = useCallback(() => navigate("/calendar/"), [navigate]);
  const navigateToDashboard = useCallback(() => navigate("/"), [navigate]);
  const navigateToMyHome = useCallback(() => navigate("/my-home/"), [navigate]);

  return (
    <Block alignCenter row flex={1} paddingHorizontal={md ? 1.5 : 2.5} style={styles.content}>
      <Block alignCenter row flex={1}>
        {backButtonVisible && <HeaderLeft onPress={handleBackPress} />}

        {showMenu && (
          <Touchable
            activeOpacity={isWeb ? 0.6 : 1}
            onPress={navigateToLanding}
            style={backButtonVisible && { marginLeft: 10 }}
          >
            <SvgIcon
              color={palette.primary.accent}
              name="stepappLogo"
              size={showMobileNavigation ? 17 : 20}
              style={showMobileNavigation ? styles.mobileLogo : styles.desktopLogo}
            />
          </Touchable>
        )}

        {!showMobileNavigation && showMenu && (
          <PickCityButton onPress={() => setModalVisible(true)} />
        )}

        {isAuthenticated && !showMobileNavigation && showMenu && (
          <Block row marginLeft={4.5}>
            <HeaderButton label={t("components.tabBar.calendar")} onPress={navigateToCalendar} />
            <HeaderButton
              label={t("components.tabBar.dashboard")}
              onPress={navigateToDashboard}
              underline
            />
            <HeaderButton label={t("components.tabBar.myHome")} onPress={navigateToMyHome} />
          </Block>
        )}
      </Block>

      {showMenu && (
        <HeaderDropdownButton items={navigationItems} showMobileNavigation={showMobileNavigation} />
      )}
    </Block>
  );
};

export default HeaderContent;

const styles = StyleSheet.create({
  content: {
    maxWidth: 1160,
    width: "100%",
    margin: "auto",
  },
  desktopLogo: {
    marginLeft: -12,
    marginRight: spacing.unit * 3,
  },
  mobileLogo: {
    marginRight: spacing.unit * 3,
    marginLeft: -20,
  },
});
